/**
 * Image Processing Utilities
 * Handles EXIF removal and image optimization for AI chat with robust timeout/retry
 */

/**
 * Timeout wrapper for promises
 * @param {Promise} promise - Promise to wrap with timeout
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise} Promise that rejects if timeout is exceeded
 */
const withTimeout = (promise, timeoutMs) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Tempo limite excedido ao processar imagem')), timeoutMs)
    )
  ]);
};

/**
 * Remove EXIF metadata from image file with timeout and retry
 * @param {File} file - Original image file
 * @param {number} retries - Number of retry attempts (default: 2)
 * @param {number} timeoutMs - Timeout in milliseconds (default: 8000)
 * @returns {Promise<string>} Base64 data URL without EXIF metadata
 */
export const removeExifMetadata = async (file, retries = 2, timeoutMs = 8000) => {
  const attemptConversion = () => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          try {
            // Create canvas to redraw image without metadata
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            
            // Convert to data URL - this strips all EXIF data
            const cleanDataUrl = canvas.toDataURL(file.type || 'image/png', 0.95);
            resolve(cleanDataUrl);
          } catch (error) {
            reject(new Error('Erro ao converter imagem para base64'));
          }
        };
        
        img.onerror = () => {
          reject(new Error('Erro ao processar imagem'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('Erro ao ler arquivo de imagem'));
      };
      
      reader.readAsDataURL(file);
    });
  };

  // Attempt conversion with timeout and retry logic
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await withTimeout(attemptConversion(), timeoutMs);
    } catch (error) {
      if (attempt === retries) {
        // Last attempt failed - throw error to be caught by caller
        throw error;
      }
      // Wait briefly before retry
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
};

/**
 * Process multiple images and remove EXIF metadata with graceful failure handling
 * @param {Array} images - Array of image objects with file property
 * @returns {Promise<Object>} Object with processedImages array and failedImages array
 */
export const processImagesForUpload = async (images) => {
  const processedImages = [];
  const failedImages = [];
  
  for (const imageObj of images) {
    try {
      const cleanDataUrl = await removeExifMetadata(imageObj?.file);
      processedImages?.push({
        ...imageObj,
        content: cleanDataUrl,
        preview: cleanDataUrl
      });
    } catch (error) {
      console.warn(`Falha ao processar ${imageObj?.name}:`, error?.message);
      // Add to failed list instead of throwing - allows text-only response
      failedImages?.push({
        name: imageObj?.name,
        reason: error?.message || 'Erro desconhecido'
      });
    }
  }
  
  return {
    processedImages,
    failedImages
  };
};